"use client";

import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { OrderStatus } from "@/types/models/order";
import { Filter, ChevronDown } from "lucide-react";

export interface OrderFilters {
  status?: OrderStatus;
  created_after?: string;
  created_before?: string;
  min_total?: number;
  max_total?: number;
  payment_status?: string;
  order_number?: number;
  customer_phone?: string;
  customer_email?: string;
  customer_name?: string;
}

interface OrderFilterModalProps {
  isOpen: boolean;
  onClose: () => void;
  onApplyFilters: (filters: OrderFilters) => void;
  initialFilters?: OrderFilters;
}

const paymentStatusOptions = [
  { value: "PENDING", label: "Pending" },
  { value: "PAID", label: "Paid" },
  { value: "FAILED", label: "Failed" },
  { value: "REFUNDED", label: "Refunded" },
];

const orderStatusOptions = [
  { value: "CREATED", label: "Created" },
  { value: "CONFIRMED", label: "Confirmed" },
  { value: "SHIPPING", label: "Shipping" },
  { value: "RETURNING", label: "Returning" },
  { value: "COMPLETED", label: "Completed" },
  { value: "CANCELLED", label: "Cancelled" },
];

export function OrderFilterModal({
  isOpen,
  onClose,
  onApplyFilters,
  initialFilters = {},
}: OrderFilterModalProps) {
  const [filters, setFilters] = useState<OrderFilters>(initialFilters);
  const [expandedSections, setExpandedSections] = useState({
    general: true,
    dates: false,
    amount: false,
    customer: false,
  });

  const toggleSection = (section: keyof typeof expandedSections) => {
    setExpandedSections((prev) => {
      const isCurrentlyExpanded = prev[section];
      // Close all sections first
      const allClosed = {
        general: false,
        dates: false,
        amount: false,
        customer: false,
      };
      // If the clicked section was closed, open it. If it was open, keep all closed.
      return {
        ...allClosed,
        [section]: !isCurrentlyExpanded,
      };
    });
  };

  const handleFilterChange = (key: keyof OrderFilters, value: any) => {
    setFilters((prev) => ({
      ...prev,
      [key]: value || undefined,
    }));
  };

  const handleApply = () => {
    // Remove empty values
    const cleanFilters = Object.fromEntries(
      Object.entries(filters).filter(
        ([, value]) => value !== "" && value !== undefined && value !== null,
      ),
    );
    onApplyFilters(cleanFilters);
    onClose();
  };

  const handleClear = () => {
    setFilters({});
  };

  const getActiveFilterCount = () => {
    return Object.values(filters).filter(
      (value) => value !== "" && value !== undefined && value !== null,
    ).length;
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto bg-card border border-border/40 shadow-2xl">
        <DialogHeader className="flex flex-row items-center justify-between pb-4 border-b border-border/60">
          <div className="flex items-center gap-2">
            <div className="p-1.5 bg-primary/10 rounded-md">
              <Filter className="h-4 w-4 text-primary" />
            </div>
            <DialogTitle className="text-lg font-semibold text-foreground">
              Filter Orders
            </DialogTitle>
          </div>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* General Filters */}
          <div className="bg-white/80 dark:bg-background rounded-lg border border-border/40 overflow-hidden">
            <button
              onClick={() => toggleSection("general")}
              className="flex items-center justify-between w-full text-left p-3 hover:bg-blue-50/60 dark:hover:bg-primary/20 transition-colors group"
            >
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                <h3 className="text-sm font-medium text-foreground">
                  General Filters
                </h3>
              </div>
              <ChevronDown
                className={`h-4 w-4 text-muted-foreground transition-transform ${
                  expandedSections.general ? "rotate-180" : ""
                }`}
              />
            </button>
            {expandedSections.general && (
              <div className="p-4 bg-white/80 dark:bg-background border-t border-border/40 space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-medium text-foreground mb-2">
                      Order Status
                    </label>
                    <select
                      value={filters.status || ""}
                      onChange={(e) =>
                        handleFilterChange("status", e.target.value)
                      }
                      className="w-full p-2.5 text-xs bg-card border border-border/60 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary transition-all shadow-sm"
                    >
                      <option value="">All Statuses</option>
                      {orderStatusOptions.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-foreground mb-2">
                      Payment Status
                    </label>
                    <select
                      value={filters.payment_status || ""}
                      onChange={(e) =>
                        handleFilterChange("payment_status", e.target.value)
                      }
                      className="w-full p-2.5 text-xs bg-card border border-border/60 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary transition-all shadow-sm"
                    >
                      <option value="">All Payment Status</option>
                      {paymentStatusOptions.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-medium text-foreground mb-2">
                    Order Number
                  </label>
                  <Input
                    type="number"
                    value={filters.order_number || ""}
                    onChange={(e) =>
                      handleFilterChange(
                        "order_number",
                        e.target.value ? Number(e.target.value) : "",
                      )
                    }
                    placeholder="Search by order number"
                    className="bg-card border-border/60 focus:ring-primary/50 focus:border-primary text-xs h-9 shadow-sm"
                  />
                </div>
              </div>
            )}
          </div>

          {/* Date Filters */}
          <div className="bg-white/80 dark:bg-background rounded-lg border border-border/40 overflow-hidden">
            <button
              onClick={() => toggleSection("dates")}
              className="flex items-center justify-between w-full text-left p-3 hover:bg-blue-50/60 dark:hover:bg-primary/20 transition-colors group"
            >
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                <h3 className="text-sm font-medium text-foreground">
                  Date Range
                </h3>
              </div>
              <ChevronDown
                className={`h-4 w-4 text-muted-foreground transition-transform ${
                  expandedSections.dates ? "rotate-180" : ""
                }`}
              />
            </button>
            {expandedSections.dates && (
              <div className="p-4 bg-white/80 dark:bg-background border-t border-border/40 space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-medium text-foreground mb-2">
                      From Date
                    </label>
                    <Input
                      type="date"
                      value={filters.created_after || ""}
                      onChange={(e) =>
                        handleFilterChange("created_after", e.target.value)
                      }
                      className="bg-card border-border/60 focus:ring-primary/50 focus:border-primary text-xs h-9 shadow-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-foreground mb-2">
                      To Date
                    </label>
                    <Input
                      type="date"
                      value={filters.created_before || ""}
                      onChange={(e) =>
                        handleFilterChange("created_before", e.target.value)
                      }
                      className="bg-card border-border/60 focus:ring-primary/50 focus:border-primary text-xs h-9 shadow-sm"
                    />
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Amount Filters */}
          <div className="bg-white/80 dark:bg-background rounded-lg border border-border/40 overflow-hidden">
            <button
              onClick={() => toggleSection("amount")}
              className="flex items-center justify-between w-full text-left p-3 hover:bg-blue-50/60 dark:hover:bg-primary/20 transition-colors group"
            >
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 bg-orange-500 rounded-full"></div>
                <h3 className="text-sm font-medium text-foreground">
                  Order Amount
                </h3>
              </div>
              <ChevronDown
                className={`h-4 w-4 text-muted-foreground transition-transform ${
                  expandedSections.amount ? "rotate-180" : ""
                }`}
              />
            </button>
            {expandedSections.amount && (
              <div className="p-4 bg-white/80 dark:bg-background border-t border-border/40 space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-medium text-foreground mb-2">
                      Minimum Amount
                    </label>
                    <Input
                      type="number"
                      value={filters.min_total || ""}
                      onChange={(e) =>
                        handleFilterChange(
                          "min_total",
                          e.target.value ? Number(e.target.value) : "",
                        )
                      }
                      placeholder="₹0"
                      className="bg-card border-border/60 focus:ring-primary/50 focus:border-primary text-xs h-9 shadow-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-foreground mb-2">
                      Maximum Amount
                    </label>
                    <Input
                      type="number"
                      value={filters.max_total || ""}
                      onChange={(e) =>
                        handleFilterChange(
                          "max_total",
                          e.target.value ? Number(e.target.value) : "",
                        )
                      }
                      placeholder="₹10,000"
                      className="bg-card border-border/60 focus:ring-primary/50 focus:border-primary text-xs h-9 shadow-sm"
                    />
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Customer Filters */}
          <div className="bg-white/80 dark:bg-background rounded-lg border border-border/40 overflow-hidden">
            <button
              onClick={() => toggleSection("customer")}
              className="flex items-center justify-between w-full text-left p-3 hover:bg-blue-50/60 dark:hover:bg-primary/20 transition-colors group"
            >
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 bg-purple-500 rounded-full"></div>
                <h3 className="text-sm font-medium text-foreground">
                  Customer Information
                </h3>
              </div>
              <ChevronDown
                className={`h-4 w-4 text-muted-foreground transition-transform ${
                  expandedSections.customer ? "rotate-180" : ""
                }`}
              />
            </button>
            {expandedSections.customer && (
              <div className="p-4 bg-white/80 dark:bg-background border-t border-border/40 space-y-4">
                <div>
                  <label className="block text-xs font-medium text-foreground mb-2">
                    Customer Name
                  </label>
                  <Input
                    type="text"
                    value={filters.customer_name || ""}
                    onChange={(e) =>
                      handleFilterChange("customer_name", e.target.value)
                    }
                    placeholder="Search by customer name"
                    className="bg-card border-border/60 focus:ring-primary/50 focus:border-primary text-xs h-9 shadow-sm"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-medium text-foreground mb-2">
                      Phone Number
                    </label>
                    <Input
                      type="text"
                      value={filters.customer_phone || ""}
                      onChange={(e) =>
                        handleFilterChange("customer_phone", e.target.value)
                      }
                      placeholder="Search by phone"
                      className="bg-card border-border/60 focus:ring-primary/50 focus:border-primary text-xs h-9 shadow-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-foreground mb-2">
                      Email Address
                    </label>
                    <Input
                      type="email"
                      value={filters.customer_email || ""}
                      onChange={(e) =>
                        handleFilterChange("customer_email", e.target.value)
                      }
                      placeholder="Search by email"
                      className="bg-card border-border/60 focus:ring-primary/50 focus:border-primary text-xs h-9 shadow-sm"
                    />
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="flex items-center justify-between pt-4 border-t border-border/60 bg-muted/20 rounded-lg p-4 mt-4">
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              onClick={handleClear}
              className="text-muted-foreground hover:text-foreground hover:bg-muted/60 text-sm h-8 px-3 rounded-lg"
            >
              Clear All
            </Button>
            {getActiveFilterCount() > 0 && (
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 bg-primary rounded-full"></div>
                <span className="text-sm text-muted-foreground">
                  {getActiveFilterCount()} filter
                  {getActiveFilterCount() !== 1 ? "s" : ""} active
                </span>
              </div>
            )}
          </div>
          <div className="flex items-center gap-3">
            <Button
              variant="outline"
              onClick={onClose}
              className="text-sm h-8 px-4 border-border/60 hover:bg-muted/40 rounded-lg"
            >
              Cancel
            </Button>
            <Button
              onClick={handleApply}
              className="bg-primary text-primary-foreground hover:bg-primary/90 text-sm h-8 px-4 shadow-md rounded-lg"
            >
              Apply Filters
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
